/* I dati della fattura, campo per campo, di sola lettura.
 *
 * Dal 25 settembre 2026 la fattura elettronica la manda Fatture Smart, e chi
 * fattura la compila a mano. **Dal 15 ottobre 2026 sera i venti tasti Copia di
 * questa scheda non ci sono piu'**: la critica a scala li ha giudicati un
 * gesto per campo che nessuno fa (in studio si incolla un testo solo, nel
 * campo Descrizione). Restano i dati per controllare e "Copia tutto"; il
 * resto del lavoro e' in `TestoFattura`. Gli importi arrivano dal server gia'
 * scritti in italiano, come vanno incollati: questa pagina non formatta e non somma.
 *
 * **Il tasto Copia deve funzionare anche in studio, non solo qui.**
 * `navigator.clipboard` esiste solo in un contesto sicuro: su 127.0.0.1 c'e',
 * su http:// con l'indirizzo di rete dello studio no. Senza il ripiego il
 * tasto funzionerebbe sulla macchina di chi sviluppa e sarebbe muto su quella
 * di chi lo usa.
 */

import { useState } from "react";
import { documenti } from "../api";
import { useCarica } from "../carica";
import type { CampoScheda, Scheda } from "../dominio";

export async function copiaNegliAppunti(testo: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(testo);
      return true;
    } catch {
      // ricade sotto
    }
  }
  const area = document.createElement("textarea");
  area.value = testo;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  let riuscito = false;
  try {
    riuscito = document.execCommand("copy");
  } catch {
    riuscito = false;
  }
  document.body.removeChild(area);
  return riuscito;
}

export function TastoCopia({ testo, etichetta = "Copia" }: { testo: string; etichetta?: string }) {
  const [esito, setEsito] = useState<"" | "ok" | "no">("");

  async function vai() {
    const riuscito = await copiaNegliAppunti(testo);
    setEsito(riuscito ? "ok" : "no");
    setTimeout(() => setEsito(""), 1600);
  }

  return (
    <button type="button" className="tasto tasto-copia" onClick={vai} aria-live="polite">
      {esito === "ok" ? "Copiato" : esito === "no" ? "Non riuscito" : etichetta}
    </button>
  );
}

function Campi({ titolo, campi }: { titolo: string; campi: CampoScheda[] }) {
  return (
    <div className="copia-sezione">
      <h4>{titolo}</h4>
      <table className="tabella copia-tabella">
        <tbody>
          {campi.map((c) => (
            <tr key={c.etichetta}>
              <td className="copia-etichetta">{c.etichetta}</td>
              <td>
                <span className="copia-valore">{c.valore}</span>
                {c.nota && <span className="narrativa">{c.nota}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Le righe come gli altri campi, una per valore da copiare: il tasto Copia
 * resta in fondo alla riga come nel resto della scheda, invece di finire sotto
 * il testo in due punti diversi. */
function campiDelleRighe(s: Scheda): CampoScheda[] {
  return s.righe.flatMap((r, i) => [
    {
      etichetta: s.righe.length > 1 ? `Riga ${i + 1}, descrizione` : "Descrizione",
      valore: r.descrizione,
      nota: "",
    },
    { etichetta: s.righe.length > 1 ? `Riga ${i + 1}, importo` : "Importo", valore: r.importo, nota: `IVA ${r.iva}` },
  ]);
}

export function SchedaCopia({ identificativo }: { identificativo: string }) {
  const scheda = useCarica<Scheda>(() => documenti.scheda(identificativo), [identificativo]);
  const s = scheda.dati;

  return (
    <section className="copia" aria-label="I dati della fattura">
      <div className="copia-testa">
        <h3>I dati della fattura</h3>
        {s && <TastoCopia testo={s.testo} etichetta="Copia tutto" />}
      </div>

      {scheda.errore && (
        <p className="errore" role="alert">
          {scheda.errore}
        </p>
      )}
      {s === undefined ? (
        scheda.errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <>
          {s.da_guardare.length > 0 && (
            <div className="segnalazioni">
              {s.da_guardare.map((testo) => (
                <p key={testo} className="riga-avviso">
                  {testo.charAt(0).toUpperCase() + testo.slice(1)}
                </p>
              ))}
            </div>
          )}

          <Campi titolo="Documento" campi={s.documento} />
          <Campi titolo="Cliente" campi={s.cliente} />

          <Campi titolo="Righe" campi={campiDelleRighe(s)} />
          <Campi titolo="Riepilogo" campi={s.riepilogo} />
          <Campi titolo="Pagamento" campi={s.pagamento} />
        </>
      )}
    </section>
  );
}
